import BarcodeImg from "../../../static/images/barcode_image.png";
import BarcodeManager from "../../../components/BarcodeManager";

import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Modal,
  NumberInput,
  Select,
  Text,
  Tooltip,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconBox,
  IconDownload,
  IconPrinter,
} from "@tabler/icons";
import AppPageHeader from "components/AppPageHeader";
import FloatingMenu from "components/FloatingMenu";
import { PrintModal } from "components/PrintModal";
import { MantineReactTable } from "mantine-react-table";
import { getCounterId, hasPermission, isCounterAdmin, isManager } from "pages/Auth/permissionGuard";
import { counters_api } from "pages/master/counter/counter.service";
import { getAlteredCounters } from "pages/master/customers/helperFunctions";
import { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useQuery } from "react-query";
import { useNavigate } from "react-router";
import { useReactToPrint } from "react-to-print";
import {
  getBodyContent,
  getCsvContent,
  getCustomId,
  getFields,
  getFilterPayload,
  getHeaders,
  getTotal,
} from "services/filterHelperFunctions";
import { api_filter } from "services/master.service";

import StockTransferDetailsModal from "./StockTransferDetailsModal";
import { stock_apis } from "./stock-transfer.service";
import { showErrorToast } from "../../../utilities/Toast";

export default function StockReceived() {
  const navigate = useNavigate();

  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
  const [barcodeModalData, setBarcodeModalData] = useState([]);
  const [barcodeProductList, setBarcodeProductList] = useState([]);
  const [barcodeBatchList, setBarcodeBatchList] = useState([]);

  const [stockId, setStockId] = useState(null);
  const [head, setHead] = useState([]);
  const [body, setBody] = useState([]);
  const [foot, setFoot] = useState([]);
  const [csv, setCsv] = useState([]);
  const [isCsv, setIsCsv] = useState(false);
  let isMobileScreen = useMediaQuery("(max-width: 768px)");

  const [orderDetailsModalVisible, setOrderDetailsModalVisible] = useState(false);
  const [selectedStockTransferId, setSelectedStockTransferId] = useState(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [filename, setFilename] = useState("file");

  const [counter, setCounter] = useState(getCounterId() || "");
  const [countersList, setCountersList] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);

  const [qtyModalOpen, setQtyModalOpen] = useState(false);
  const [tempProducts, setTempProducts] = useState([]);
  const [fullDataCache, setFullDataCache] = useState(null);

  useEffect(() => {
    setPagination(p => ({ ...p, pageIndex: 0 }));
  }, [columnFilters]);

  useQuery({
    queryKey: ["counters-data-list-received"],
    queryFn: () => counters_api.get(),
    onSuccess: data => setCountersList(getAlteredCounters(data.data)),
  });

  const stock_data = useQuery({
    queryKey: ["stock_list_received", pagination.pageIndex, pagination.pageSize, counter, columnFilters],
    queryFn: async () => {
      if (columnFilters.length > 0) {
        return printFilter(false);
      }
      return stock_apis.get_stock_list(counter, pagination.pageIndex + 1, pagination.pageSize);
    },
    keepPreviousData: true,
    // Query runs when any counter is set (counter_admin or manager with warehouse counter).
    enabled: !!counter,
  });

  const generateBarcodeWithQty = () => {
    const products = tempProducts;
    const stock_id = fullDataCache?.stock_transfer_id || stockId;

    const codes = products.flatMap(p =>
      Array.from({ length: p.stock }).map(() => `${p.product_code}-${p.batch_no}`)
    );

    const productList = products.map(p => ({
      product_code: p.product_code,
      title: p.product,
      stock_id: stock_id,
    }));

    const batchList = products.map(p => ({
      id: p.batch_id,
      product_code: p.product_code,
      batch_no: p.batch_no,
      mrp: p.mrp,
      stock_id: stock_id,
    }));

    setBarcodeModalData(codes);
    setBarcodeProductList(productList);
    setBarcodeBatchList(batchList);
    setQtyModalOpen(false);
    setBarcodeModalOpen(true);
  };

  const columns = useMemo(
    () => [
      {
        accessorFn: row => row.id,
        id: "id",
        header: "Stock ID",
        accessorKey: "id",
        size: 80,
        Cell: ({ cell }) => <Text fw={500}>{cell.getValue()}</Text>,
        filterVariant: "text",
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue) return true;
          return row.getValue(columnId)?.toString() === filterValue.toString();
        },
      },
      {
        accessorFn: row => new Date(row.created_at),
        id: "created_at",
        header: "Date",
        accessorKey: "created_at",
        size: 50,
        Cell: ({ cell }) => cell.getValue().toLocaleDateString("en-GB"),
        filterVariant: "date-range",
      },
      {
        header: "Transferred From",
        accessorKey: "from_counter_name",
      },
      {
        header: "Transferred To",
        accessorKey: "to_counter_name",
      },
      {
        header: "Amount",
        accessorKey: "amount",
        filterVariant: "range",
        Cell: ({ cell }) => {
          const value = Number(cell.getValue() || 0);
          return <span>₹ {value.toFixed(2)}</span>;
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: ["created", "accepted", "discrepancy", "rejected", "loss_report"],
        },
        Cell: ({ cell }) => {
          const status = cell.getValue();
          const color =
            status === "rejected"
              ? "red"
              : status === "discrepancy"
              ? "yellow"
              : status === "accepted"
              ? "green"
              : status === "loss_report"
              ? "grape"
              : "gray";
          return <Badge color={color}>{status.replace("_", " ")}</Badge>;
        },
      },
    ],
    []
  );

  const componentRef = useRef();
  const handlePrint = useReactToPrint({ content: () => componentRef.current });

  useEffect(() => {
    if (body.length !== 0 && head.length !== 0) {
      if (!isCsv) {
        handlePrint();
        setBody([]);
        setHead([]);
      }
    }
    setIsCsv(false);
  }, [body, head]);

  const printFilter = async (forCsv = true) => {
    const model = "StockList";
    const appendData = [
      { id: "id", append: "", replace_id: "id" },
      { id: "created_at", append: "" },
      { id: "name", append: "from_counter__", replace_id: "from_counter_name" },
      { id: "name", append: "to_counter__", replace_id: "to_counter_name" },
      { id: "amount", append: "" },
      { id: "status", append: "" },
    ];

    const custom = getCustomId(columns, columnFilters, appendData);
    const filterPayload = getFilterPayload(custom?.columns, custom?.columnFilters);
    if (counter) {
      filterPayload.to_counter = counter;
    }
    const fields = getFields(custom?.columns);

    try {
      const res = await api_filter(
        model,
        filterPayload,
        fields,
        "StockListSerializer",
        counter,
        pagination.pageIndex + 1,
        pagination.pageSize,
        forCsv
      );
      if (res.success) {
        const body_content = getBodyContent(res.data, fields);
        if (forCsv) {
          const mapped_body_content = body_content.map(row => {
            const fromCounterId = row[2];
            const toCounterId = row[3];
            const fromCounterName = countersList?.find(c => String(c.id) === String(fromCounterId))?.label || fromCounterId;
            const toCounterName = countersList?.find(c => String(c.id) === String(toCounterId))?.label || toCounterId;
            
            const newRow = [...row];
            newRow[2] = fromCounterName;
            newRow[3] = toCounterName;
            return newRow;
          });

          const head_content = getHeaders(columns);
          const csv_content = getCsvContent(head_content, mapped_body_content, ["STOCK_RECEIVED"]);
          const foot_content = getTotal(mapped_body_content, [4]);
          setFilename(
            `STOCK_RECEIVED_${countersList?.find(e => e.id === counter)?.label}_${new Date().toLocaleDateString()}.csv`
          );
          setCsv(csv_content);
          setHead(head_content);
          setBody(mapped_body_content);
          setFoot(foot_content);
        } else {
          return { data: res.data, count: res.count };
        }
      } else {
        showErrorToast({ title: "Error", message: res.message });
        return { data: [] };
      }
    } catch (e) {
      showErrorToast({ title: "Error", message: e.message });
      return { data: [] };
    }
  };

  const csvRef = useRef();
  useEffect(() => {
    if (csv.length && isCsv) {
      csvRef.current.link.click();
      setCsv([]);
      setIsCsv(false);
    }
  }, [csv]);

  return (
    <>
      <CSVLink
        style={{ display: "none" }}
        asyncOnClick={true}
        data={csv}
        filename={filename}
        ref={csvRef}
      />
      <Container fluid>
        {isMobileScreen ? (
          <Select
            p={5}
            label="Counter"
            defaultValue={counter || null}
            placeholder="Select Counter"
            data={countersList || []}
            onChange={setCounter}
            clearable
            disabled={isCounterAdmin()}
          />
        ) : (
          <AppPageHeader
            title="Stock Received"
            button={
              <Box>
                <Flex align={"end"} gap={10}>
                  <Button
                    onClick={() => {
                      setIsCsv(true);
                      printFilter();
                    }}
                    leftIcon={<IconDownload />}
                  >
                    CSV
                  </Button>
                  <Button leftIcon={<IconPrinter />} onClick={() => printFilter()}>
                    Print
                  </Button>
                  <Select
                    label="Counter"
                    defaultValue={counter || null}
                    placeholder="Select Counter"
                    data={countersList || []}
                    onChange={setCounter}
                    clearable
                    disabled={isCounterAdmin()}
                  />
                </Flex>
              </Box>
            }
          />
        )}

        <Box px={isMobileScreen ? 5 : 25} sx={{ borderRadius: 2 }} mt={10} mb={30}>
          <Box className="datatable-wrapper">
            {/* Prompt shown only if no counter could be determined */}
            {!counter ? (
              <Flex
                direction="column"
                align="center"
                justify="center"
                py={60}
                sx={theme => ({
                  border: `1px dashed ${theme.colors.gray[3]}`,
                  borderRadius: 8,
                  background: theme.colors.gray[0],
                })}
              >
                <Text size="sm" color="dimmed" mb={4}>
                  Select a counter from the dropdown above
                </Text>
                <Text size="xs" color="dimmed">
                  Stock received records will appear once a counter is selected.
                </Text>
              </Flex>
            ) : (
            <MantineReactTable
              manualFiltering
              onColumnFiltersChange={setColumnFilters}
              state={{
                pagination,
                columnFilters,
                isLoading: stock_data.isLoading || stock_data.isRefetching,
              }}
              mantineTableProps={{ className: "datatable", striped: true }}
              mantineTableBodyRowProps={({ row }) => ({
                sx: theme => ({
                  backgroundColor:
                    row.original.status === "loss_report"
                      ? theme.colorScheme === "dark"
                        ? "rgba(103, 65, 217, 0.4) !important"
                        : "rgba(224, 197, 255, 0.5) !important"
                      : undefined,
                }),
              })}
              enableRowActions
              positionActionsColumn="last"
              renderTopToolbarCustomActions={() => (
                <Box px={isMobileScreen ? 5 : 24} py={10}>
                  <Text
                    fw={600}
                    tt="uppercase"
                    size={"sm"}
                    sx={theme => ({ letterSpacing: 1, color: theme.colors.brand[7] })}
                  >
                    Received
                  </Text>
                </Box>
              )}
              renderRowActions={({ row }) => (
                <Flex>
                  {/* Barcode generation — for counter admin or manager */}
                  {(isCounterAdmin() || isManager()) &&
                    ["accepted", "discrepancy"].includes(row.original.status) && (
                      <Tooltip label="Generate Barcode">
                        <ActionIcon
                          mr={10}
                          sx={theme => ({ color: theme.colors.brand[6] })}
                          onClick={async () => {
                            try {
                              const res = await stock_apis.get_stock_transfer_details(
                                row.original.id
                              );
                              const fullData = res.data;
                              const products = fullData.stock_list || [];
                              const stock_id = fullData?.stock_transfer_id || row.original.id;
                              setTempProducts(products.map(p => ({ ...p })));
                              setFullDataCache(fullData);
                              setStockId(stock_id);
                              setQtyModalOpen(true);
                            } catch (err) {
                              showErrorToast({
                                title: "Error",
                                message: "Failed to load barcode details.",
                              });
                            }
                          }}
                        >
                          <img
                            src={BarcodeImg}
                            alt="barcode"
                            style={{ width: 30, height: 30, objectFit: "contain" }}
                          />
                        </ActionIcon>
                      </Tooltip>
                    )}

                  {/* View Details (with accept/reject for counter admin) */}
                  <Tooltip label="View Transfer Details">
                    <ActionIcon
                      mr={10}
                      sx={theme => ({ color: theme.colors.brand[6] })}
                      onClick={() => {
                        setSelectedStockTransferId(row.original.id);
                        setOrderDetailsModalVisible(true);
                      }}
                    >
                      <IconBox style={{ width: 22 }} />
                    </ActionIcon>
                  </Tooltip>
                </Flex>
              )}
              initialState={{ density: "xs" }}
              columns={columns}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableColumnActions={false}
              data={stock_data.data?.data || []}
              rowCount={stock_data.data?.count}
              enableStickyHeader
              enableGlobalFilter={false}
              onPaginationChange={updater => {
                setPagination(prev => {
                  const next = typeof updater === "function" ? updater(prev) : updater;
                  return {
                    pageIndex: next.pageIndex ?? prev.pageIndex,
                    pageSize: next.pageSize ?? prev.pageSize,
                  };
                });
              }}
              manualPagination
            />
            )}
          </Box>
        </Box>

        {/* Barcode quantity modal */}
        <Modal opened={qtyModalOpen} onClose={() => setQtyModalOpen(false)} title="Edit Quantity">
          {tempProducts.map((p, i) => (
            <NumberInput
              key={i}
              label={p.product}
              value={p.stock}
              min={1}
              max={p.stock}
              precision={["PCS", "NOS"].includes(p.units?.toUpperCase()) ? 0 : 3}
              step={["PCS", "NOS"].includes(p.units?.toUpperCase()) ? 1 : 0.001}
              onChange={val => {
                const updated = [...tempProducts];
                updated[i].stock = val || 1;
                setTempProducts(updated);
              }}
            />
          ))}
          <Button mt={10} onClick={generateBarcodeWithQty}>
            Continue
          </Button>
        </Modal>
      </Container>

      {/* Barcode manager */}
      {barcodeModalOpen && (
        <BarcodeManager
          show={barcodeModalOpen}
          onClose={() => setBarcodeModalOpen(false)}
          barcodeData={barcodeModalData}
          productList={barcodeProductList}
          batchList={barcodeBatchList}
          stockId={stockId}
        />
      )}

      {/* Details modal — with accept/reject for the receiver */}
      <StockTransferDetailsModal
        stockTransferId={selectedStockTransferId}
        opened={orderDetailsModalVisible}
        onClose={() => setOrderDetailsModalVisible(false)}
        showActions={hasPermission("change_counterstock")}
        onSuccess={() => stock_data.refetch()}
      />

      <div style={{ display: "none" }}>
        <PrintModal
          cols={[3]}
          title="Stock Received"
          head={head}
          body={body}
          foots={foot}
          ref={componentRef}
        />
      </div>

      {isMobileScreen && (
        <>
          <FloatingMenu m={5} ml={60} left size={50} onClick={() => { setIsCsv(true); printFilter(); }}>
            <IconDownload color="white" />
          </FloatingMenu>
          <FloatingMenu m={5} right size={50} onClick={() => printFilter()}>
            <IconPrinter color="white" />
          </FloatingMenu>
        </>
      )}
    </>
  );
}
